import type { UseQueryResult } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'

import type { BidType, MarketProgram } from '@/features/bidding/constants'
import type {
  PricePrefillBidsQueryParams,
  PricePrefillBidsResponse,
} from '@/features/bidding/endpoints/pricePrefillBids'
import { getPricePrefillBids } from '@/features/bidding/endpoints/pricePrefillBids'
import { useErrorHandler } from '@/features/bidding/hooks/useErrorHandler'
import type { MarketDate } from '@/features/bidding/utils/date/marketDate'
import type { UseQueryOptions } from '@/types/queries'

const GET_PRICE_PREFILL_BIDS_API_ID = 'GET_PRICE_PREFILL_BIDS'

export type BidsLatestPricesQueryParams = {
  country: CountryIdentifier
  portfolio: string
  marketProgram: MarketProgram
  bidType: BidType
  untilDeliveryDay: MarketDate // retrieve latest prices taking into account the bids until this delivery day (included)
}

export type UsePricePrefillBidsQueryResult = Omit<UseQueryResult<PricePrefillBidsResponse>, 'data'> & {
  data: PricePrefillBidsResponse | undefined
}

export const usePricePrefillBidsQuery = (
  params: BidsLatestPricesQueryParams,
  options?: UseQueryOptions<PricePrefillBidsResponse>,
): UsePricePrefillBidsQueryResult => {
  const pricePrefillBidsParams: PricePrefillBidsQueryParams = {
    country: params.country,
    portfolioCode: params.portfolio,
    marketProgram: params.marketProgram,
    bidType: params.bidType,
    deliveryDay: params.untilDeliveryDay,
  }
  const queryResult = useQuery({
    ...options,
    queryKey: [GET_PRICE_PREFILL_BIDS_API_ID, pricePrefillBidsParams],
    queryFn: () => getPricePrefillBids(pricePrefillBidsParams),
  })

  useErrorHandler(queryResult.isError)

  return queryResult
}
